// /loyalty — Loyalty Dashboard (PAGES.md §3.32) — OWNER (50)+
//
// Hero + 4 KPI tiles + Program config form + Top-10 loyalty customers table.
// Palette: gold → champagne → rose (amber/warning).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Input,
  Toggle,
  Button,
  Alert,
  Avatar,
  Badge,
  Skeleton,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { ApiError } from '@/lib/api-client';
import {
  useLoyaltyConfig,
  useLoyaltyStats,
  useTopLoyaltyCustomers,
  useLoyaltyTransactions,
  useUpdateLoyaltyConfig,
} from '@/features/loyalty';
import type { LoyaltyConfig, LoyaltyTransactionRow, TopLoyaltyCustomerRow } from '@/features/loyalty';
import { ReportKpiRow, ReportTable, KpiIcons } from '@/features/reports';
import { formatPhone, formatShortDate } from '@/features/customers';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { paiseToRupeeInput, rupeeInputToPaise } from '@/utils/price-input';
import { useStoreTimezone } from '@/lib/store-clock';

type Row = TopLoyaltyCustomerRow;

function makeTopCustomerColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
  {
    key: 'rank',
    header: '#',
    render: (_v, _row, index) => (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 text-xs font-bold text-white tabular-nums shadow-sm">
        {index + 1}
      </span>
    ),
  },
  {
    key: 'name',
    header: 'Customer',
    render: (_v, row) => (
      <div className="flex items-center gap-3">
        <Avatar name={row.name} size="sm" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{row.name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{formatPhone(row.phone)}</p>
        </div>
      </div>
    ),
  },
  {
    key: 'loyalty_tier',
    header: 'Tier',
    render: (_v, row) => row.loyalty_tier ? <Badge variant="info" size="sm">{row.loyalty_tier}</Badge> : <span className="text-xs text-neutral-400">—</span>,
  },
  {
    key: 'loyalty_points',
    header: 'Points',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-bold text-amber-700 dark:text-amber-300 tabular-nums">
        {row.loyalty_points.toLocaleString('en-IN')}
      </span>
    ),
  },
  {
    key: 'last_visit_at',
    header: 'Last Visit',
    render: (_v, row) => <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{formatShortDate(row.last_visit_at)}</span>,
  },
  {
    key: 'total_spent',
    header: 'Total Spent',
    numeric: true,
    render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">{formatPaise(row.total_spent, currency)}</span>,
  },
  ];
}

const DEFAULT_CONFIG: LoyaltyConfig = {
  points_per_rupee: 0.01,
  min_redemption_points: 100,
  redemption_value_paise: 50,
  expiry_months: 12,
  is_active: false,
};

/**
 * Sign for the activity list's Points column. ADJUST and EXPIRE rows are stored
 * signed, so their sign comes from the points (CU2-010); REDEEM is always a debit
 * and EARN a credit.
 */
export function loyaltyPointsSign(row: Pick<LoyaltyTransactionRow, 'type' | 'points'>): '−' | '+' | '' {
  if (row.type === 'REDEEM') return '−';
  if (row.type === 'EARN') return '+';
  return row.points < 0 ? '−' : row.points > 0 ? '+' : '';
}

function LoyaltyDashboardContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const currencySymbol = getCurrencySymbol(currency);
  const timeZone = useStoreTimezone();
  const topCustomerColumns = useMemo(() => makeTopCustomerColumns(currency), [currency]);
  const configQ = useLoyaltyConfig();
  const statsQ = useLoyaltyStats();
  const topQ = useTopLoyaltyCustomers(10);
  const [activityFilter, setActivityFilter] = useState<'ALL' | 'REDEEM' | 'EARN'>('REDEEM');
  const txQ = useLoyaltyTransactions({
    type: activityFilter === 'ALL' ? undefined : activityFilter,
    limit: 50,
  });
  const updateConfig = useUpdateLoyaltyConfig();

  const [form, setForm] = useState<LoyaltyConfig>(DEFAULT_CONFIG);
  const [hydrated, setHydrated] = useState(false);
  // Redemption value is entered in rupees/point; form keeps the canonical paise.
  const [redemptionRupees, setRedemptionRupees] = useState(paiseToRupeeInput(DEFAULT_CONFIG.redemption_value_paise, currency));
  const configFormRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (configQ.data && !hydrated) {
      setForm(configQ.data);
      setRedemptionRupees(paiseToRupeeInput(configQ.data.redemption_value_paise, currency));
      setHydrated(true);
    }
  }, [configQ.data, hydrated, currency]);

  const isDirty = hydrated && configQ.data && JSON.stringify(form) !== JSON.stringify(configQ.data);

  async function handleSave() {
    try {
      // Re-seed from the server echo. `form` was hydrated once and then only
      // patched field-by-field, so it keeps the `earn_rate_label` it started
      // with — a value the backend recomputes from the rate on every save. The
      // whole-object compare above would therefore never match again and the
      // "Unsaved changes" badge stayed lit on a config that saved perfectly.
      // Re-seeding also absorbs anything the backend normalises (points_per_rupee
      // is NUMERIC(10,4)) and shows the canonical value rather than the raw text.
      const saved = await updateConfig.mutateAsync(form);
      setForm(saved);
      setRedemptionRupees(paiseToRupeeInput(saved.redemption_value_paise, currency));
      toast({ variant: 'success', title: 'Loyalty program saved' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Please try again.';
      toast({ variant: 'error', title: 'Save failed', description: msg });
    }
  }

  function scrollToConfig() {
    configFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const stats = statsQ.data;
  const topCustomers = topQ.data ?? [];
  const isLoading = configQ.isLoading || statsQ.isLoading;
  const notConfigured = hydrated && !configQ.data?.is_active && !form.is_active;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 p-6 sm:p-8 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(245,158,11,0.6)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-60 w-60 rounded-full bg-white/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/30 backdrop-blur-sm">
              Loyalty Program
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Rewards & Retention</h1>
            <p className="mt-1.5 text-sm sm:text-base text-white/90 max-w-xl">
              Configure points earnings, track redemptions, and celebrate your most valuable customers.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {(stats?.active_members ?? 0).toLocaleString('en-IN')} active members
              </span>
              <span className="rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 font-semibold">
                {form.is_active ? 'Program Active' : 'Program Paused'}
              </span>
              {configQ.data?.earn_rate_label ? (
                <span className="rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 font-semibold">
                  {configQ.data.earn_rate_label}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Button
              variant="secondary"
              onClick={scrollToConfig}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 backdrop-blur-sm"
            >
              Configure Program
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
            ))}
          </div>
          <Skeleton variant="rect" height={360} className="rounded-2xl mb-6" />
        </>
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              {
                id: 'members',
                title: 'Active Members',
                value: (stats?.active_members ?? 0).toLocaleString('en-IN'),
                tone: 'brand',
                icon: KpiIcons.users,
                trend: stats?.active_members_delta_pct !== undefined
                  ? {
                      value: `${Math.abs(stats.active_members_delta_pct).toFixed(1)}%`,
                      direction: stats.active_members_delta_pct >= 0 ? 'up' : 'down',
                    }
                  : undefined,
              },
              {
                id: 'issued',
                title: 'Points Issued',
                value: (stats?.total_points_issued ?? 0).toLocaleString('en-IN'),
                tone: 'warning',
                icon: KpiIcons.star,
              },
              {
                id: 'redeemed',
                title: 'Points Redeemed',
                value: (stats?.total_points_redeemed ?? 0).toLocaleString('en-IN'),
                tone: 'success',
                icon: KpiIcons.trendUp,
              },
              {
                id: 'rate',
                title: 'Redemption Rate',
                value: `${(stats?.redemption_rate_pct ?? 0).toFixed(1)}%`,
                subLabel: `Avg ${(stats?.avg_points_per_member ?? 0).toLocaleString('en-IN')} pts/member`,
                tone: 'violet',
                icon: KpiIcons.percent,
              },
            ]}
          />

          {/* Two-column: config form + top customers */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-6">
            {/* Program configuration — 3 of 5 columns */}
            <section
              ref={configFormRef}
              className="lg:col-span-3 rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-amber-900/10 dark:via-orange-900/10 dark:to-rose-900/10 dark:backdrop-blur-xl ring-1 ring-amber-100 dark:ring-amber-800/30 shadow-md p-6 sm:p-7 animate-slide-up"
            >
              <header className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <h2 className="text-base font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                    <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-amber-500 to-rose-500" />
                    Program Configuration
                  </h2>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Points earning rate and redemption rules.</p>
                </div>
                {isDirty ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-warning-100 dark:bg-warning-900/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-warning-700 dark:text-warning-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-warning-500 animate-pulse" />
                    Unsaved changes
                  </span>
                ) : null}
              </header>

              {notConfigured ? (
                <Alert
                  variant="info"
                  title="Loyalty program is not yet active"
                  description="Configure the earning rate below and flip the toggle to enable points for every bill."
                />
              ) : null}

              <div className="mt-5 rounded-xl bg-white/70 dark:bg-neutral-900/40 p-4 ring-1 ring-amber-100/60 dark:ring-amber-800/20 mb-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Enable loyalty program</p>
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      Customers earn points on every completed bill and can redeem them at checkout.
                    </p>
                  </div>
                  <Toggle checked={form.is_active} onChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label={`Points per ${currencySymbol}1`}
                  type="number"
                  step={0.01}
                  min={0}
                  value={String(form.points_per_rupee)}
                  onChange={(v) => setForm((f) => ({ ...f, points_per_rupee: Number(v) || 0 }))}
                  helperText={`e.g. 0.01 = 1 point per ${currencySymbol}100 spent`}
                />
                <Input
                  label="Min Redemption (points)"
                  type="number"
                  min={0}
                  value={String(form.min_redemption_points)}
                  onChange={(v) => setForm((f) => ({ ...f, min_redemption_points: Math.floor(Number(v)) || 0 }))}
                  helperText="Customer must hold at least this many to redeem"
                />
                <Input
                  label={`Redemption Value (${getCurrencySymbol(currency)} / point)`}
                  type="number"
                  variant="currency"
                  currency={currency}
                  min={0}
                  value={redemptionRupees}
                  onChange={(v) => {
                    setRedemptionRupees(v);
                    setForm((f) => ({ ...f, redemption_value_paise: rupeeInputToPaise(v, currency) ?? 0 }));
                  }}
                  helperText={`Current: ${formatPaise(form.redemption_value_paise, currency)} off per point`}
                />
                <Input
                  label="Point Expiry (months)"
                  type="number"
                  min={0}
                  value={String(form.expiry_months ?? 0)}
                  onChange={(v) =>
                    setForm((f) => {
                      const n = Math.floor(Number(v));
                      return { ...f, expiry_months: Number.isFinite(n) && n > 0 ? n : null };
                    })
                  }
                  helperText="0 or empty = never expires. Points expire this many months after the day they were earned."
                />
              </div>

              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  Changes apply immediately after saving. A new expiry applies to points earned from then on.
                </p>
                <Button variant="primary" onClick={handleSave} loading={updateConfig.isPending} disabled={!isDirty}>
                  Save Configuration
                </Button>
              </div>
            </section>

            {/* Top customers — 2 of 5 columns */}
            <div className="lg:col-span-2 animate-slide-up" style={{ animationDelay: '40ms' }}>
              <ReportTable<Row>
                title="Top 10 Loyalty Customers"
                subtitle="Your highest point-balance members."
                columns={topCustomerColumns}
                data={topCustomers.map((c) => ({ ...c }))}
                loading={topQ.isFetching}
                totalCount={topCustomers.length}
                emptyTitle="No loyalty customers yet"
                emptyDescription="Enable the program and complete a few sales — top members will surface here."
                stickyHeader={false}
              />
            </div>
          </div>

          {/* Recent loyalty activity (redemptions + earns) */}
          <section className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md ring-1 ring-neutral-100 dark:ring-neutral-700 p-5 sm:p-6 mb-6 animate-slide-up" style={{ animationDelay: '80ms' }}>
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-emerald-500 to-teal-500" />
                  Recent Loyalty Activity
                </h2>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Last 50 transactions — who redeemed, who earned, against which bill.</p>
              </div>
              <div className="inline-flex rounded-lg ring-1 ring-neutral-200 dark:ring-neutral-700 p-0.5 bg-neutral-50 dark:bg-neutral-900">
                {(['REDEEM', 'EARN', 'ALL'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setActivityFilter(f)}
                    className={[
                      'px-3 py-1.5 text-xs font-semibold rounded-md transition-all',
                      activityFilter === f
                        ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm'
                        : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200',
                    ].join(' ')}
                  >
                    {f === 'ALL' ? 'All' : f === 'REDEEM' ? 'Redemptions' : 'Earns'}
                  </button>
                ))}
              </div>
            </header>

            {txQ.isFetching && !txQ.data ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} variant="rect" height={48} className="rounded-lg" />
                ))}
              </div>
            ) : (txQ.data ?? []).length === 0 ? (
              <div className="py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
                {activityFilter === 'REDEEM'
                  ? 'No redemptions yet. Customers redeem points by paying with WALLET at checkout.'
                  : activityFilter === 'EARN'
                  ? 'No points earned yet. Enable the program and complete a bill with a known customer.'
                  : 'No loyalty activity yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-700">
                      <th className="py-2 pr-3 font-semibold">When</th>
                      <th className="py-2 pr-3 font-semibold">Customer</th>
                      <th className="py-2 pr-3 font-semibold">Type</th>
                      <th className="py-2 pr-3 font-semibold text-right">Points</th>
                      <th className="py-2 pr-3 font-semibold text-right">Balance After</th>
                      <th className="py-2 pr-3 font-semibold">Bill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(txQ.data ?? []).map((row: LoyaltyTransactionRow) => {
                      const isRedeem = row.type === 'REDEEM';
                      const isEarn = row.type === 'EARN';
                      const sign = loyaltyPointsSign(row);
                      const tone = isRedeem
                        ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                        : isEarn
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200';
                      return (
                        <tr
                          key={row.id}
                          className="border-b border-neutral-50 dark:border-neutral-800 even:bg-neutral-50/40 dark:even:bg-neutral-900/30 hover:bg-amber-50/60 dark:hover:bg-amber-900/10 transition-colors"
                        >
                          <td className="py-2.5 pr-3 text-xs text-neutral-500 dark:text-neutral-400 tabular-nums whitespace-nowrap">
                            {new Date(row.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone })}
                          </td>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <Avatar name={row.customer_name} size="sm" />
                              <div className="min-w-0">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/customers/${row.customer_id}`)}
                                  className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 hover:text-amber-600 dark:hover:text-amber-400 truncate text-left"
                                >
                                  {row.customer_name}
                                </button>
                                {row.customer_phone ? (
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{formatPhone(row.customer_phone)}</p>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 pr-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}>
                              {row.type}
                            </span>
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums font-bold">
                            <span className={sign === '−' ? 'text-rose-600 dark:text-rose-300' : sign === '+' ? 'text-emerald-600 dark:text-emerald-300' : 'text-neutral-700 dark:text-neutral-200'}>
                              {sign}{Math.abs(row.points).toLocaleString('en-IN')}
                            </span>
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-700 dark:text-neutral-300">
                            {row.balance_after.toLocaleString('en-IN')}
                          </td>
                          <td className="py-2.5 pr-3 text-xs">
                            {row.bill_number ? (
                              <span className="font-mono text-neutral-600 dark:text-neutral-300">{row.bill_number}</span>
                            ) : (
                              <span className="text-neutral-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {!topCustomers.length && !topQ.isFetching ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-10 animate-fade-in">
              <EmptyState
                variant="page"
                title={notConfigured ? 'Loyalty program not configured' : 'No loyalty activity yet'}
                description={notConfigured ? 'Configure the earning rate above and enable the program to start rewarding your customers.' : 'Enable the program and complete a few sales — top members will surface here.'}
                action={notConfigured ? { label: 'Configure Loyalty', onClick: scrollToConfig } : { label: 'View Customers', onClick: () => navigate('/customers') }}
                icon={
                  <svg className="h-20 w-20 text-amber-300 dark:text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l2.39 7.36H22l-6.19 4.5 2.39 7.36L12 16.72 5.8 21.22l2.39-7.36L2 9.36h7.61z" />
                  </svg>
                }
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function LoyaltyDashboardPage() {
  return (
    <PageContainer title="Loyalty" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Loyalty' }]}>
      <ErrorBoundary resetKey="loyalty-dashboard">
        <LoyaltyDashboardContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
